import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  Menu, X, Phone, ChevronRight, ChevronLeft, Mail, MapPin, 
  Coffee, Flame, Snowflake, Wind, Droplets, 
  ChefHat, Zap, RotateCcw, Scissors, Thermometer, 
  Shield, RefreshCw, Filter
} from 'lucide-react';
import { SERVICES, PARTNERS } from './data';

const iconMap = {
  Coffee, Flame, Snowflake, Wind, Droplets, 
  ChefHat, Zap, RotateCcw, Scissors, Thermometer, 
  Shield, RefreshCw, Filter
};

function useScrollReveal() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  return [ref, isVisible];
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isProduktionOpen, setIsProduktionOpen] = useState(false);

  return (
    <nav className="bg-slate-900 text-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="block">
            <img 
              src="/imgi_20_Bon-Multi-service_Final_transparant-white-150x68.png" 
              alt="BON Multi Service" 
              className="h-16 w-auto"
            />
          </Link>
          
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-lime-500 transition-all duration-300 ease-in-out">Hjem</Link>
            <Link to="/hvem-er-vi" className="hover:text-lime-500 transition-all duration-300 ease-in-out">Hvem vi er</Link>
            
            <div 
              className="relative group h-full flex items-center"
              onMouseEnter={() => setIsProduktionOpen(true)}
              onMouseLeave={() => setIsProduktionOpen(false)}
            >
              <button className="flex items-center gap-1 hover:text-lime-500 transition-colors cursor-pointer font-medium">
                Produktion <ChevronRight size={16} className={`rotate-90 transition-transform ${isProduktionOpen ? 'rotate-[270deg]' : ''}`} />
              </button>
              
              {isProduktionOpen && (
                <div className="absolute top-full left-0 w-56 bg-white text-slate-900 shadow-xl rounded-b-lg overflow-hidden flex flex-col border-t-2 border-lime-500">
                  <Link to="/storkoekken" className="px-4 py-3 hover:bg-slate-50 transition-all duration-300 border-b border-slate-100" onClick={() => { setIsProduktionOpen(false); setIsOpen(false); }}>Storkøkken</Link>
                  <Link to="/vvs" className="px-4 py-3 hover:bg-slate-50 transition-all duration-300 border-b border-slate-100" onClick={() => { setIsProduktionOpen(false); setIsOpen(false); }}>VVS</Link>
                  <Link to="/service-og-reparation" className="px-4 py-3 hover:bg-slate-50 transition-all duration-300" onClick={() => { setIsProduktionOpen(false); setIsOpen(false); }}>Service & reparation</Link>
                </div>
              )}
            </div>

            <Link to="/hvorfor-vaelge-os" className="hover:text-lime-500 transition-all duration-300 ease-in-out">Hvorfor vælge os</Link>
            <Link to="/kontakt" className="bg-lime-600 hover:bg-lime-700 text-white px-6 py-2 rounded-full flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:shadow-lime-600/20 active:scale-95">
              <Phone size={18} />
              +45 24 64 95 37
            </Link>
          </div>

          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2">
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>
      
      {isOpen && (
        <div className="md:hidden absolute top-20 left-0 w-full bg-slate-900 border-t border-slate-800 shadow-2xl animate-in slide-in-from-top duration-300">
          <div className="flex flex-col p-6 space-y-6">
            <Link to="/" onClick={() => setIsOpen(false)} className="text-lg font-medium border-b border-slate-800 pb-2">Hjem</Link>
            <Link to="/hvem-er-vi" onClick={() => setIsOpen(false)} className="text-lg font-medium border-b border-slate-800 pb-2">Hvem vi er</Link>
            
            <div className="flex flex-col space-y-4">
              <button 
                onClick={() => setIsProduktionOpen(!isProduktionOpen)}
                className="flex items-center justify-between text-lg font-medium text-lime-500 border-b border-slate-800 pb-2"
              >
                Produktion <ChevronRight size={20} className={`transition-transform duration-300 ${isProduktionOpen ? 'rotate-90' : ''}`} />
              </button>
              
              {isProduktionOpen && (
                <div className="flex flex-col space-y-4 pl-4 animate-in fade-in slide-in-from-left-2 duration-200">
                  <Link to="/storkoekken" onClick={() => setIsOpen(false)} className="text-slate-300 hover:text-lime-500 transition-all duration-300">Storkøkken</Link>
                  <Link to="/vvs" onClick={() => setIsOpen(false)} className="text-slate-300 hover:text-lime-500 transition-all duration-300">VVS</Link>
                  <Link to="/service-og-reparation" onClick={() => setIsOpen(false)} className="text-slate-300 hover:text-lime-500 transition-all duration-300">Service & reparation</Link>
                </div>
              )}
            </div>

            <Link to="/hvorfor-vaelge-os" onClick={() => setIsOpen(false)} className="text-lg font-medium border-b border-slate-800 pb-2">Hvorfor vælge os</Link>
            
            <Link to="/kontakt" onClick={() => setIsOpen(false)} className="bg-lime-600 text-white text-center py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-lime-900/20 active:scale-95 transition-all">
              <Phone size={20} /> +45 24 64 95 37
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

function Hero() {
  const slides = [
    { image: "/hero-kitchen.jpg", alt: "Professionelt Køkken" },
    { image: "/hero-technician.jpg", alt: "Teknisk Service" },
    { image: "/service-vvs.jpg", alt: "VVS Installation" }
  ];

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const prevSlide = () => setCurrent(current === 0 ? slides.length - 1 : current - 1);
  const nextSlide = () => setCurrent(current === slides.length - 1 ? 0 : current + 1);

  return (
    <section className="relative h-[600px] bg-slate-900 overflow-hidden">
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === current ? "opacity-40 scale-110" : "opacity-0 scale-100"
            }`}
          >
            <img 
              src={slide.image} 
              className="w-full h-full object-cover" 
              alt={slide.alt}
            />
          </div>
        ))}
        <div className="absolute bottom-0 right-0 w-1/2 h-20 bg-white rounded-tl-[100px] z-20 hidden md:block"></div>
        
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex flex-col justify-center items-start text-white">
        <h1 className="text-5xl md:text-7xl font-bold mb-6 max-w-2xl leading-tight">
          Velkommen til <br/><span className="text-lime-500">Bon Multi Service</span>
        </h1>
        <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-xl">
          Vi leverer driftssikre løsninger inden for professionelle køkkenmiljøer og VVS til både erhverv og private.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/kontakt" className="bg-lime-600 hover:bg-lime-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:shadow-xl hover:shadow-lime-600/30 active:scale-95">
            KONTAKT OS
          </Link>
          <Link to="/vi-tilbyder" className="flex items-center gap-2 text-white hover:text-lime-500 font-bold transition-all duration-300 px-8 py-4 group">
            LÆS MERE <ChevronRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Home() {
  const [brandsRef, brandsVisible] = useScrollReveal();
  const [aboutRef, aboutVisible] = useScrollReveal();
  const [servicesRef, servicesVisible] = useScrollReveal();
  const [contactRef, contactVisible] = useScrollReveal();

  useEffect(() => {
    document.title = "BON Multi Service";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '');
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', '');
      document.head.appendChild(metaDescription);
    }

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', '');
    } else {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      ogImage.setAttribute('content', '');
      document.head.appendChild(ogImage);
    }
  }, []);

  return (
    <div className="flex flex-col">
      <Hero />
      
      {/* Brands Section */}
      <section 
        ref={brandsRef}
        className={`bg-white py-16 border-b border-slate-200 transition-all duration-1000 ${
          brandsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Vi forhandler</h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Storkøkkener, hårde hvidevarer, VVS-service og montering af førende mærker.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 items-center justify-items-center">
            {PARTNERS.map((p, index) => (
              <div key={index} className="w-full h-24 flex items-center justify-center p-6 bg-white rounded-lg shadow-sm border border-slate-100">
                <img 
                  src={p.logo} 
                  alt={p.name} 
                  className="max-h-full max-w-full object-contain" 
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        ref={aboutRef}
        className={`py-24 bg-slate-50 overflow-hidden transition-all duration-1000 delay-200 ${
          aboutVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl bg-white">
                <img 
                  src="/about-service.jpg" 
                  alt="BON Multi Service Arbejde" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-lime-600 text-white p-8 rounded-2xl hidden lg:block">
                <p className="text-4xl font-bold">30+</p>
                <p className="text-sm font-medium uppercase tracking-wider">Års erfaring</p>
              </div>
            </div>
            <div>
              <span className="text-lime-600 font-bold uppercase tracking-wider text-sm">Bon Multi Service</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2 mb-6">Hvem vi er</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Vi arbejder inden for VVS og storkøkkener. Vi har 30 års erfaring inden for branchen og hos os er du altid sikret en god kvalitet på dit køkken.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Vi servicerer både restauranter, caféer, institutioner og private hjem med fokus på driftssikkerhed og kvalitet. Vores ekspertise spænder vidt fra komplekse storkøkken-installationer til præcis VVS-service i private boliger.
              </p>
              <Link to="/hvem-er-vi" className="inline-flex items-center gap-2 bg-lime-600 text-white px-8 py-4 rounded-full font-bold hover:bg-lime-700 transition-all duration-300 shadow-lg shadow-lime-600/20 active:scale-95 group">
                LÆS MERE <ChevronRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section 
        ref={servicesRef}
        className={`py-24 bg-slate-900 text-white transition-all duration-1000 ${
          servicesVisible ? "opacity-100 scale-95" : "opacity-0 scale-90"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Vi tilbyder</h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Vi forhandler næsten alt og alle mærker inden for storkøkkenudstyr, vvs-service og montering. 
              Vi tilbyder også brugt udstyr til skarpe priser.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.slice(0, 12).map(service => {
              const IconComp = iconMap[service.icon] || ChefHat;
              return (
                <div 
                  key={service.id} 
                  className="p-8 bg-slate-800/50 rounded-2xl border border-slate-700 flex flex-col items-center text-center"
                >
                  <div className="mb-6 w-16 h-16 rounded-2xl bg-slate-700 flex items-center justify-center">
                    <IconComp size={32} className="text-lime-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <div className="h-1 w-8 bg-slate-700"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section Preview */}
      <section 
        ref={contactRef}
        className={`py-12 bg-white border-t border-gray-100 transition-all duration-1000 ${
          contactVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">Har du brug for hjælp?</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Uanset om det er en akut reparation, en fast serviceaftale eller montering af din nye Quooker, står vi klar til at hjælpe.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a href="tel:+4524649537" className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-blue-700 transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/20 active:scale-95">
              <Phone size={24} /> +45 24 64 95 37
            </a>
            <Link to="/kontakt" className="flex items-center justify-center bg-slate-900 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-slate-800 transition-all duration-300 hover:shadow-lg hover:shadow-slate-900/20 active:scale-95">
              Send en besked
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function SubpageHero({ title, description, image = "/hero-kitchen.jpg" }) {
  return (
    <section className="relative h-[400px] bg-slate-900 overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0">
        <img 
          src={image} 
          className="w-full h-full object-cover opacity-40 scale-105 animate-pulse-slow" 
          alt={title}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 text-center text-white">
        <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">{title}</h1>
        {description && (
          <p className="text-xl text-slate-300 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-200">
            {description}
          </p>
        )}
      </div>
    </section>
  );
}

function About() {
  return (
    <div className="flex flex-col">
      <SubpageHero 
        title="Om Bon Multi Service" 
        description="Kvalitet og driftssikkerhed i over 30 år."
        image="/about-service.jpg"
      />

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Din foretrukne partner inden for VVS og storkøkken</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                Bon Multi Service er en teknisk servicevirksomhed med speciale i salg, service og montering af storkøkkenudstyr, VVS-installationer og hårde hvidevarer. 
                Vi har mere end 30 års erfaring i branchen, hvilket er din garanti for korrekt rådgivning og udførelse.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Vi servicerer i dag en bred vifte af kunder, herunder restauranter, caféer, hoteller, kantiner og institutioner, som alle er afhængige af driftssikkert udstyr. 
                Samtidig hjælper vi mange private boligejere med alt fra Quooker-installationer til vandbehandling og generelle VVS-opgaver.
              </p>
              <div className="grid grid-cols-2 gap-8 py-6 border-y border-slate-100">
                <div>
                  <p className="text-3xl font-bold text-lime-600">30+</p>
                  <p className="text-sm font-medium text-slate-500 uppercase">Års erfaring</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-lime-600">100%</p>
                  <p className="text-sm font-medium text-slate-500 uppercase">Dansk ejet</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/about-service.jpg" 
                alt="Vores arbejde" 
                className="rounded-2xl shadow-2xl w-full object-cover aspect-square"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white p-8 rounded-2xl hidden lg:block">
                <p className="font-bold italic text-lg leading-snug">
                  "Vi går aldrig på kompromis <br/>med kvaliteten af vores arbejde."
                </p>
                <p className="mt-4 text-lime-500 font-bold">— Bent Ole Nielsen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-12">Hvorfor vælge os?</h2>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="text-lime-600" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-4">Driftssikkerhed</h3>
              <p className="text-slate-600">Vi ved, at nedetid koster penge. Derfor fokuserer vi på holdbare løsninger og hurtig service.</p>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ChefHat className="text-lime-600" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-4">Ekspertise</h3>
              <p className="text-slate-600">Med 30 år i branchen har vi set det meste og kan løse selv de mest komplekse udfordringer.</p>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Droplets className="text-lime-600" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-4">Optimering</h3>
              <p className="text-slate-600">Vi er specialister i vand- og kalkoptimering, der forlænger levetiden på dit udstyr markant.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Contact() {
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (response.ok) alert('Tak for din henvendelse! Vi vender tilbage hurtigst muligt.');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 text-slate-900">Kontakt os</h1>
        <div className="grid md:grid-cols-2 gap-16">
          <form onSubmit={handleSubmit} className="space-y-6">
            <input name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Navn</label>
              <input required name="name" type="text" className="w-full border-slate-300 rounded-lg p-3 border focus:ring-2 focus:ring-lime-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input required name="email" type="email" className="w-full border-slate-300 rounded-lg p-3 border focus:ring-2 focus:ring-lime-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Besked</label>
              <textarea required name="message" rows="4" className="w-full border-slate-300 rounded-lg p-3 border focus:ring-2 focus:ring-lime-500 focus:border-transparent outline-none"></textarea>
            </div>
            <button type="submit" className="w-full bg-lime-600 text-white font-bold py-4 rounded-full hover:bg-lime-700 transition-all">SEND BESKED</button>
          </form>
          <div className="bg-slate-900 text-white p-10 rounded-2xl">
            <h3 className="text-2xl font-bold mb-6">Info</h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <MapPin className="text-lime-500 shrink-0" />
                <p>BON Multi Service<br/>Skovlunde Byvej 30A 1, 2740 Skovlunde</p>
              </div>
              <div className="flex gap-4">
                <Phone className="text-lime-500 shrink-0" />
                <div>
                  <p>44 84 72 14</p>
                  <p>Mobil: 24 64 95 37</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="text-lime-500 shrink-0" />
                <div>
                  <p>bon@multiservice.com</p>
                  <p>bentole@webspeed.dk</p>
                </div>
              </div>
              <div className="pt-8 border-t border-slate-800">
                <p className="text-slate-400 text-sm">CVR: 31730023</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="relative bg-slate-900 text-white py-12 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img 
          src="/footer-bg.jpg" 
          alt="Background" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div>
            <img 
              src="/imgi_20_Bon-Multi-service_Final_transparant-white-150x68.png" 
              alt="BON Multi Service" 
              className="h-12 w-auto mb-6"
            />
            <div className="space-y-3 text-sm">
              <div className="flex gap-3 items-center">
                <MapPin size={16} />
                <span>Skovlunde Byvej 30A 1, 2740 Skovlunde</span>
              </div>
              <div className="flex gap-3 items-start">
                <Phone size={16} className="mt-1" />
                <div>
                  <p>Mobil: 44 84 72 14</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-4 flex-shrink-0" />
                <div>
                  <p>BON Multi Service</p>
                  <p>bentole@webspeed.dk</p>
                  <p>bon@multiservice.com</p>
                </div>
              </div>
              <div className="flex gap-3 items-start text-slate-400 text-xs mt-2">
                <div className="w-4 flex-shrink-0" />
                <div>CVR: 31730023</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:items-end">
            <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium md:justify-end mb-6">
              <Link to="/" className="hover:text-lime-500 transition-colors">Forside</Link>
              <Link to="/vvs" className="hover:text-lime-500 transition-colors">VVS</Link>
              <Link to="/storkoekken" className="hover:text-lime-500 transition-colors">Storkøkken</Link>
              <Link to="/service-og-reparation" className="hover:text-lime-500 transition-colors">Service & reparation</Link>
              <Link to="/kontakt" className="hover:text-lime-500 transition-colors">Kontakt</Link>
            </nav>
            <p className="text-xs text-slate-400">© {new Date().getFullYear()} BON Multi Service. Alle rettigheder forbeholdes.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function WhyChooseUs() {
  return (
    <div className="flex flex-col">
      <SubpageHero 
        title="Hvorfor vælge os?" 
        description="Gennem mere end 30 år har vi opbygget en position som en af branchens mest pålidelige partnere."
        image="/hero-technician.jpg"
      />

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6">
                <Shield className="text-lime-600" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Driftssikkerhed</h3>
              <p className="text-slate-600 leading-relaxed">
                Vi ved, at i et professionelt køkken er nedetid lig med tabt omsætning. Derfor fokuserer vi på løsninger, der holder, og yder hurtig service, når uheldet er ude.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6">
                <ChefHat className="text-lime-600" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-slate-900">30 års erfaring</h3>
              <p className="text-slate-600 leading-relaxed">
                Vores lange historie i branchen betyder, at vi har set og løst næsten alle tænkelige udfordringer inden for både VVS og storkøkkenmiljøer.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6">
                <Droplets className="text-lime-600" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Vandoptimering</h3>
              <p className="text-slate-600 leading-relaxed">
                Vi er eksperter i kalk- og vandbehandling. Korrekt vandbehandling forlænger levetiden på dit udstyr og sikrer den bedste smag i kaffen og det reneste service.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">Klar til at optimere din drift?</h2>
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            Kontakt os i dag for en uforpligtende snak om, hvordan vi kan hjælpe din virksomhed eller dit hjem.
          </p>
          <Link to="/kontakt" className="inline-block bg-lime-600 hover:bg-lime-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all">
            FÅ ET TILBUD
          </Link>
        </div>
      </section>
    </div>
  );
}

function Storkoekken() {
  return (
    <div className="flex flex-col">
      <SubpageHero 
        title="Storkøkken" 
        description="Professionelle løsninger til det moderne industrikøkken."
        image="/hero-kitchen.jpg"
      />
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Totalleverandør af storkøkkenudstyr</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                Vi forhandler næsten alt og alle mærker inden for storkøkkenudstyr. Uanset om du driver en restaurant, café, hotel eller en stor offentlig institution, har vi ekspertisen til at rådgive dig om det rette udstyr.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Vores sortiment dækker alt fra kraftfulde ovne og komfurer til avancerede røremaskiner, pålægsmaskiner og komplette opvaskesystemer. Vi fokuserer altid på udstyr, der kan tåle den hårde drift i et professionelt miljø.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                  <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center text-lime-600">
                    <ChefHat size={20} />
                  </div>
                  <span className="font-semibold text-slate-700">Køkkenmaskiner</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                  <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center text-lime-600">
                    <Flame size={20} />
                  </div>
                  <span className="font-semibold text-slate-700">Ovne & grill</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img src="/hero-kitchen.jpg" className="rounded-2xl shadow-xl w-full aspect-video object-cover" alt="Storkøkken" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function VVS() {
  return (
    <div className="flex flex-col">
      <SubpageHero 
        title="VVS-Løsninger" 
        description="Faglig stolthed og moderne VVS-arbejde til erhverv og private."
        image="/service-vvs.jpg"
      />
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 relative">
              <img src="/service-vvs.jpg" className="rounded-2xl shadow-xl w-full aspect-video object-cover" alt="VVS Arbejde" />
            </div>
            <div className="order-1 md:order-2 space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Driftssikre VVS-installationer</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                Vi udfører alle former for VVS-service og montering. Fra den lille dryppende vandhane i privaten til komplekse vand- og afløbsinstallationer i industrikøkkener.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Vi er specialister i vandoptimering og kalkbehandling. Korrekt vandbehandling er essentielt for at beskytte dine dyre maskiner mod kalkskader og sikre en optimal vandkvalitet til både madlavning og maskindrift.
              </p>
              <div className="flex flex-col gap-4 pt-4">
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <Droplets className="text-blue-600 shrink-0" size={24} />
                  <div>
                    <h4 className="font-bold text-blue-900 text-sm uppercase">Specialister i Quooker</h4>
                    <p className="text-blue-800 text-sm">Vi monterer og servicerer Quooker-systemer til både private og erhverv.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceRepair() {
  return (
    <div className="flex flex-col">
      <SubpageHero 
        title="Service og Reparation" 
        description="Hurtig hjælp og professionel vedligeholdelse af dine køkkenfaciliteter."
        image="/hero-technician.jpg"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Vi klarer alt inden for reparationer og service af udstyr</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                Vi har specialiseret os i reparationer af forskellige typer og størrelser af køkkener. Derfor kan vi både hjælpe private med deres køkkener samt med industrikøkkener for erhvervslivet.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Derudover kan vi stå for montering af alt udstyr til køkkener samt serviceaftaler, så du altid er sikret hjælp, hvis der skulle opstå uforudsete problemer med dit køkken. Specielt i industrikøkkenet kan det være en kæmpe udgift, hvis udstyret går i stykker og det ikke er til at få fat i kvalificeret arbejdskraft til at reparere det.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Jo længere tid der går inden det kan blive repareret, jo flere penge kan man nå at miste i indtjening. Når du laver en serviceaftale hos os, er du sikret hurtig hjælp.
              </p>
              <div className="pt-6">
                <Link to="/kontakt" className="inline-flex items-center gap-2 bg-lime-600 text-white px-8 py-4 rounded-full font-bold hover:bg-lime-700 transition-all shadow-lg shadow-lime-600/20">
                  KONTAKT OS FOR SERVICE <ChevronRight size={20} />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/hero-technician.jpg" 
                alt="Tekniker udfører reparation" 
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white p-8 rounded-2xl hidden lg:block">
                <p className="font-bold text-lg leading-snug">
                  Sikr din drift med en <br/><span className="text-lime-500 text-2xl">Serviceaftale</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8 text-slate-900">Vil du vide mere?</h2>
          <p className="text-xl text-slate-600 mb-12 leading-relaxed">
            Kontakt os gerne, hvis du vil vide mere om vores serviceaftaler, montering af køkken eller reparationer. Vi står altid klar til at rådgive om den bedste løsning for dig.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a href="tel:+4524649537" className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/10">
              <Phone size={24} /> +45 24 64 95 37
            </a>
            <Link to="/kontakt" className="flex items-center justify-center bg-slate-900 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10">
              Send en besked
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Immediate scroll to top when path changes for a snappier feel
    // but allowing the CSS scroll-behavior: smooth to handle it if preferred.
    // Using instant here often feels better during page transitions to avoid 
    // the user seeing the previous page's scroll position.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-lime-200 selection:text-lime-900 scroll-smooth">
      <ScrollToTop />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/hvem-er-vi" element={<About />} />
          <Route path="/storkoekken" element={<Storkoekken />} />
          <Route path="/vvs" element={<VVS />} />
          <Route path="/hvorfor-vaelge-os" element={<WhyChooseUs />} />
          <Route path="/service-og-reparation" element={<ServiceRepair />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
    </div>
  )
}

export default App
